import React, { Component, Fragment } from 'react';
import cx from 'classnames';
import gql from 'graphql-tag';
import { compose } from '../../reactApolloCompat';
import withStyles from '@mui/styles/withStyles';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Hidden from '../Hidden';
import TableData from '../form/TableData';
import TextField from '../form/TextField';
import { prettyPrice } from '../../util/strings';
import cloneDeep from 'lodash.clonedeep';
import { GST, PreloadProducts, price_amendment_range, ProductConfig } from '../../util/products';
import { OutlineButton, SaveButton } from '../form/PrimaryButton';
import Grid from '../form/Grid';
import { getClient, getUtilitiesClient } from '../../apollo';
import { PurchaseOrderBooking, PurchaseOrderObject } from '../../page/fragments/PurchaseOrders';
import LineItemAutoComplete from '../form/LineItemAutoComplete';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import { getProperty, isNullOrUndefined, limitRange, round } from '../../util/objects';
import Spinner from '../Spinner';
import { lookupItemSource } from '../../page/quotes/quotes-modal/QuotesModal';
import { WhiteLoader } from '../WhiteLoader';
import { isRelatedObjectDefined } from '../../util/bookable';
import { ProductPriceOverrides, VariationPriceOverrides } from '../../page/fragments/Product';
import { CloseIcon, DeleteIcon, UsdCircleIcon } from '../IconIndex';

class PurchaseOrderModal extends Component {
    state = {
        busy: false,
        sendingXero: false,
        submitBtnDisabled: true,
        booking: null,
        templatesLoaded: false, // preset bill from billTemplate
        canAlter: false // if booking already has items, can't add additional fees.
    };

    static getDerivedStateFromProps(nextProps, oldState) {
        if (!nextProps.open) return { templatesLoaded: false, booking: null, sendingXero: false };
        if (!oldState.booking) {
            if (!!nextProps.booking) {
                // copy booking data from props into bill.
                const booking = cloneDeep(nextProps.booking);
                const items =
                    !!booking.BookingItems && booking.BookingItems.edges
                        ? booking.BookingItems.edges.map(e => e.node)
                        : booking.BookingItems || [];
                booking.BookingItems = PurchaseOrderModal.convertSalesToCosts(items, nextProps.funeralKey);
                const noItems = !(items.length > 0 && items.some(e => Number(e.Quantity) > 0));
                return {
                    booking,
                    submitBtnDisabled: noItems,
                    canAlter: !items || items.length < 1 || !!nextProps.canAdd
                };
            }
            return { booking: null, submitBtnDisabled: true };
        }
        return null;
    }

    static reCalcTable(data) {
        const subtotal = data.Qty * 1 * (!!data.needGST ? data.Price / (GST + 1) : data.Price);
        data.SubTotal = round(subtotal);
        data.GST = !!data.needGST ? (data.SubTotal * GST).toFixed(2) : 0;
        data.Total = Number(data.Qty * data.Price).toFixed(2);
        return data;
    }

    static convertSalesToCosts = (bookingItems, funeralKey) => {
        if (bookingItems && Array.isArray(bookingItems)) {
            return (
                bookingItems.map(selected => {
                    const item = {
                        ID: null,
                        ProductID: !!selected.Product ? selected.Product.ID : null,
                        Product: selected.Product || null,
                        VariationID: !!selected.Variation ? selected.Variation.ID : null,
                        Variation: selected.Variation || null
                    };
                    const source = lookupItemSource(selected, funeralKey, true);
                    item.UnitPrice = source.GST ? (GST + 1) * source.Cost : source.Cost; // PO items include GST
                    item.Quantity = selected.Qty || selected.Quantity || 1;
                    item.Title = source.Title;
                    return item;
                }) || []
            );
        }
        return [];
    };

    componentDidUpdate(prevProps, prevState) {
        const that = this;
        const { booking, busy, templatesLoaded } = that.state;
        const { contact, funeralKey } = that.props;
        if (isNullOrUndefined(booking)) return;
        if (
            (isNullOrUndefined(booking.BookingItems) || booking.BookingItems.length === 0) &&
            !templatesLoaded &&
            !!contact
        ) {
            const presets = async () => {
                return await getUtilitiesClient().query({
                    query: BillTemplatesQuery,
                    variables: { ContactID: contact.ID }
                });
            };

            presets().then(({ data }) => {
                const presetBooking = cloneDeep(booking);
                if (!!data && !!data.readBillTemplates && !!data.readBillTemplates.edges) {
                    const myTemplate = data.readBillTemplates.edges.find(
                        e => e.node.Contact.ID === contact.ID && !e.node.NotCurrent
                    );
                    if (!!myTemplate) {
                        presetBooking.BookingItems = myTemplate.node.BillTemplateItems.map(selected => {
                            const item = {
                                ID: null,
                                ProductID: !!selected.Product ? selected.Product.ID : null,
                                Product: selected.Product || null,
                                VariationID: !!selected.Variation ? selected.Variation.ID : null,
                                Variation: selected.Variation || null
                            };
                            const source = lookupItemSource(selected, funeralKey, true);
                            item.UnitPrice = source.GST ? (GST + 1) * source.Cost : source.Cost; // PO items include GST
                            item.Quantity = !selected.Optional ? selected.Qty : 0;
                            item.Title = source.Title;
                            return item;
                        });
                    }
                }
                const result = { templatesLoaded: true, canAlter: true };
                if (presetBooking && !!presetBooking.BookingItems && presetBooking.BookingItems.length > 0) {
                    result.booking = presetBooking;
                    result.submitBtnDisabled = false;
                    that.setState(result);
                }
            });
        }

        const unloadedProducts = booking.BookingItems.filter(e =>
            isNullOrUndefined(ProductConfig.productMap[e.ProductID])
        );
        if (unloadedProducts.length > 0) {
            if (!busy) {
                that.setState({ busy: true }, () =>
                    PreloadProducts(that, unloadedProducts).then(() => {
                        const { booking: wasBooking } = that.state;
                        const BookingItems = PurchaseOrderModal.convertSalesToCosts(
                            wasBooking.BookingItems || [],
                            funeralKey
                        );
                        that.setState({ busy: false, booking: { ...wasBooking, BookingItems } });
                    })
                );
            }
        }
    }

    render() {
        const { classes, open, className } = this.props;
        if (!!open)
            return (
                <Dialog
                    classes={{ paper: cx(className, classes.root, 'dialog-modal modal') }}
                    open={open}
                    aria-labelledby={'PurchaseOrderModal'}
                >
                    <form noValidate onSubmit={e => this.onSubmit(e)}>
                        {this.renderForm()}
                    </form>
                </Dialog>
            );
        return null;
    }

    renderForm() {
        const { classes, label, contact } = this.props;
        const { booking, sendingXero, submitBtnDisabled, canAlter } = this.state;
        const items = (booking && booking.BookingItems) || [];
        const noItems = !(items.length > 0 && items.some(e => Number(e.Quantity) > 0));
        return (
            <Fragment>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        overflow: 'hidden',
                        pointerEvents: 'none'
                    }}
                >
                    <UsdCircleIcon
                        style={{
                            opacity: 0.025,
                            width: 300,
                            height: 300,
                            position: 'absolute',
                            right: -24,
                            top: 124,
                            transform: 'rotate(20deg)',
                            zIndex: 0
                        }}
                    />
                </div>
                <DialogTitle className={classes.diagHeader}>
                    <Hidden smDown>
                        <UsdCircleIcon className={classes.starButton} />
                        <div className={classes.diagHeaderTitle}>
                            {label || 'Supplier'}:<strong> {contact.Name}</strong>
                        </div>
                        <div className={classes.diagHeaderSubtitle}>Create a new Bill</div>
                    </Hidden>
                    <Hidden smUp>
                        <div className={classes.diagHeaderTitle}>Create Bill for {label || 'Supplier'}</div>
                    </Hidden>
                </DialogTitle>
                <DialogContent className={classes.content}>
                    <Grid container spacing={2} pc={1} style={{ margin: 0 }}>
                        <Grid item xs={12}>
                            {canAlter && this.renderServiceLookup()}
                            {!!booking &&
                                !!booking.BookingItems &&
                                booking.BookingItems.length > 0 &&
                                this.renderAdjustmentList()}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className={cx(classes.diagActions, 'modal-footer', 'paper')}>
                    <div>
                        <OutlineButton onClick={() => this.onCancel()}>
                            <CloseIcon />
                            <Hidden smDown>Cancel</Hidden>
                        </OutlineButton>
                    </div>
                    <SaveButton type="submit" disabled={sendingXero || submitBtnDisabled || noItems}>
                        {sendingXero ? (
                            <Fragment>
                                <Spinner />
                                <Hidden smDown>Submitting...</Hidden>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <UsdCircleIcon />
                                <Hidden smDown>Submit Bill</Hidden>
                            </Fragment>
                        )}
                    </SaveButton>
                </DialogActions>
            </Fragment>
        );
    }

    renderServiceLookup() {
        const { booking } = this.state;
        const { funeralKey } = this.props;
        return (
            <Fragment>
                <LineItemAutoComplete
                    isPurchase
                    variations
                    label={'Search for a product or service fee...'}
                    onSelect={selected => {
                        const item = {
                            ID: null,
                            ProductID: !!selected.Product ? selected.Product.ID : null,
                            Product: selected.Product || null,
                            VariationID: !!selected.Variation ? selected.Variation.ID : null,
                            Variation: selected.Variation || null
                        };
                        const source = lookupItemSource(selected, funeralKey, true);
                        item.UnitPrice = source.GST ? (GST + 1) * source.Cost : source.Cost; // PO items include GST
                        item.Quantity = 1;
                        item.Title = source.Title;
                        if (booking) {
                            const list = booking.BookingItems || [];
                            list.push(item);
                            booking.BookingItems = list;
                        }
                        this.setState({ submitBtnDisabled: false, booking });
                    }}
                />
                {!(!!booking && !!booking.BookingItems && !!booking.BookingItems.length > 0) ? (
                    <p
                        style={{
                            fontSize: '0.875rem',
                            fontStyle: 'italic',
                            color: '#999'
                        }}
                    >
                        Please search for a product or service fee to add to this bill.
                    </p>
                ) : (
                    <br />
                )}
            </Fragment>
        );
    }

    onRemoveItem = rowIndex => {
        const { booking } = this.state;
        const newItems = booking.BookingItems.toSpliced(rowIndex, 1);
        this.setState({ booking: { ...booking, BookingItems: newItems } });
    };

    onChangeQty(rowIndex, stringValue) {
        const { booking } = this.state;
        if (
            isNullOrUndefined(booking) ||
            isNullOrUndefined(booking.BookingItems) ||
            isNullOrUndefined(booking.BookingItems[rowIndex])
        )
            return;
        booking.BookingItems[rowIndex].Quantity = stringValue;
        this.setState({ booking });
    }

    onChangePrice(rowIndex, stringValue) {
        const { booking } = this.state;

        if (
            isNullOrUndefined(booking) ||
            isNullOrUndefined(booking.BookingItems) ||
            isNullOrUndefined(booking.BookingItems[rowIndex])
        )
            return;
        booking.BookingItems[rowIndex].Price = stringValue;
        this.setState({ booking });
    }

    onBlurPrice(rowIndex, stringValue) {
        const { booking } = this.state;

        if (
            isNullOrUndefined(booking) ||
            isNullOrUndefined(booking.BookingItems) ||
            isNullOrUndefined(booking.BookingItems[rowIndex])
        )
            return;

        const limitedValue = limitRange(
            stringValue || 0,
            booking.BookingItems[rowIndex].UnitPrice,
            price_amendment_range,
            true
        );
        if (isNaN(limitedValue)) {
            return;
        }
        booking.BookingItems[rowIndex].Price = round(limitedValue).toFixed(2); // reformat for nicer price
        this.setState({ booking });
    }

    renderAdjustmentList() {
        const { classes, canEdit, disabled, funeralKey } = this.props;
        const { booking, canAlter, busy } = this.state;

        const { productMap } = ProductConfig;
        let orderTotal = 0;
        const data = booking.BookingItems
            //product might not be loaded
            .filter(e => !isNullOrUndefined(productMap[e.ProductID]) && !!e.Product)
            .map(e => {
                const useVariation = (e.VariationID > 0 || (e.Variation && e.Variation.ID > 0)) && e.Variation;
                const needGST = !!useVariation ? e.Variation.ITC : e.Product.ITC;
                const originalCost = lookupItemSource(e, funeralKey, true);
                const Cost = needGST ? originalCost.Cost * (GST + 1) : originalCost.Cost;
                const Price = isNullOrUndefined(e.Price) ? round(e.UnitPrice).toFixed(2) : e.Price;
                const displayData = {
                    data: {
                        Code: useVariation ? e.Variation.InternalItemID : e.Product.InternalItemID,
                        Description: e.Title || productMap[e.ProductID].title,
                        Cost,
                        Price,
                        Qty: Number(Number(e.Quantity).toFixed(0)),
                        needGST: !!needGST
                    }
                };
                displayData.data = PurchaseOrderModal.reCalcTable(displayData.data);
                orderTotal += Number(displayData.data.Price) * Number(displayData.data.Qty);
                return displayData.data;
            });

        const columns = [
            { id: 'Code', label: 'Item Code/SKU' },
            { id: 'Description', label: 'Description of product or service' },
            {
                id: 'Cost',
                label: 'Value',
                render: val => prettyPrice(val),
                styles: { textAlign: 'right' }
            },
            {
                id: 'Price',
                label: 'Price',
                render: (val, row, rowIndex) => {
                    const gstPrice = data[rowIndex].Price;
                    return (
                        <TextField
                            InputProps={{ startAdornment: <InputAdornment position={'start'}>$</InputAdornment> }}
                            onChange={e => this.onChangePrice(rowIndex, e.target.value)}
                            onBlur={e => this.onBlurPrice(rowIndex, e.target.value)}
                            style={{ width: 111, margin: '-8px 0' }}
                            type="number"
                            value={gstPrice}
                        />
                    );
                },
                styles: { width: 1 }
            },
            {
                id: 'Qty',
                label: 'Quantity',
                render: (val, row, rowIndex) =>
                    canAlter ? (
                        <TextField
                            onChange={e => this.onChangeQty(rowIndex, e.target.value)}
                            style={{ width: 66, margin: '-8px 0' }}
                            type="number"
                            value={val}
                        />
                    ) : (
                        val
                    ),
                styles: { textAlign: 'right', width: 1 }
            },
            // {
            //     id: 'SubTotal',
            //     label: 'Subtotal (exGST)',
            //     render: val => prettyPrice(val),
            //     styles: { textAlign: 'right', width: '5.5rem' }
            // },
            {
                id: 'GST',
                label: 'GST',
                render: (val, row) => (!!row.needGST ? prettyPrice(val) : 'FREE'),
                styles: { textAlign: 'right', width: '5rem' }
            },
            {
                id: 'Total',
                label: 'Total',
                render: val => <b>{prettyPrice(val)}</b>,
                styles: { textAlign: 'right', width: '5.5rem' }
            },
            {
                id: 'Kill',
                label: '',
                render: (val, row, rowIndex) => (
                    <IconButton
                        className={classes.iconButton}
                        onClick={e => this.onRemoveItem(rowIndex)}
                        text={'Remove'}
                    >
                        <DeleteIcon className={classes.icon} disabled={!canAlter} />
                    </IconButton>
                )
            }
        ];

        if (!canEdit || disabled)
            columns.forEach((e, i, a) => {
                if (e.id === 'Price') delete a[i];
            });

        //if (!canAlter) columns.pop();

        return (
            <div style={{ position: 'relative' }}>
                {!!busy && <WhiteLoader />}
                <Typography variant={'display1'}>Bill Total: {prettyPrice(orderTotal)}</Typography>
                <div className={classes.dataList}>
                    <TableData
                        columns={columns}
                        data={data}
                        footer={[
                            { id: 'Qty' /*'SubTotal'*/, label: 'TOTAL' },
                            {
                                id: 'GST',
                                render: (column, data) => {
                                    const sum = data
                                        .map(obj => {
                                            return 1 * obj['GST'];
                                        })
                                        .reduce((sum, obj) => {
                                            return sum + obj;
                                        });
                                    return prettyPrice(sum);
                                }
                            },
                            {
                                id: 'Total',
                                render: (column, data) => {
                                    const sum = data
                                        .map(obj => {
                                            return 1 * obj['Total'];
                                        })
                                        .reduce((sum, obj) => {
                                            return sum + obj;
                                        });
                                    return <b>{prettyPrice(sum)}</b>;
                                }
                            }
                        ]}
                    />
                </div>
            </div>
        );
    }

    onCancel = reload => {
        this.props.onClose(reload);
        //this.clearState();
    };

    onSubmit(e) {
        e.preventDefault();

        const { funeralID, mutation, purchaseOrderField, eventObj } = this.props;
        const { booking, sendingXero } = this.state;

        if (sendingXero) return;

        this.setState({ sendingXero: true });

        createPurchaseOrder(booking, eventObj, purchaseOrderField, funeralID, mutation).then(
            mutation => {
                if (mutation.data) {
                    const payloadProperty = Object.keys(mutation.data)[0];
                    const payload = mutation.data[payloadProperty];
                    const purchaseOrder = getProperty(payload, purchaseOrderField);

                    if (!purchaseOrder) {
                        console.error('unexpected result: ', mutation.data);
                        this.onGqlError('Um, server returned no data?', 'no data returned');
                        return;
                    }

                    return submitToXero(purchaseOrder.ID).then(
                        moreData => {
                            if (moreData && moreData.data.submitPurchaseOrderToXero) {
                                this.props.setSnackbarMessage('Success, your bill was submitted.', true);
                                this.onCancel(true);
                            }
                        },
                        subError => {
                            this.onGqlError('Created bill, but failed to submit to xero.', subError);
                            this.onCancel(true);
                        }
                    );
                }

                this.onGqlError('Um, server returned no data?', 'no data returned');
            },
            error => this.onGqlError('Failed to create bill.', error)
        );
    }

    onGqlError(action, error) {
        this.setState({ sendingXero: false });
        if (error) console.error(error);
        this.props.setSnackbarMessage(action, false, null, new Error(error));
    }
}

const submitToXeroMutation = gql`
    ${PurchaseOrderObject}
    mutation SubmitToXero($id: ID!) {
        submitPurchaseOrderToXero(ID: $id) {
            ...PurchaseOrderObject
        }
    }
`;

const submitToXero = async ID => {
    let asyncQuery;

    asyncQuery = await getClient().mutate({
        mutation: submitToXeroMutation,
        variables: {
            id: ID
        }
    });

    return asyncQuery;
};

const createPurchaseOrder = async (booking, eventObj, purchaseOrderField, funeralId, mutation) => {
    const input = {
        [purchaseOrderField]: {
            FuneralID: funeralId,
            Products: booking.BookingItems.map(e => {
                let UnitPrice = e.Price; // stringValue includes GST
                if (isNullOrUndefined(UnitPrice)) {
                    UnitPrice = e.UnitPrice;
                }
                UnitPrice = Number(UnitPrice);
                return {
                    ProductID: !!e.Product ? e.Product.ID : e.ProductID,
                    VariationID: !!e.Variation ? e.Variation.ID : e.VariationID,
                    Qty: Number(Number(e.Quantity).toFixed(0)),
                    UnitPrice
                };
            }).filter(e => Number(e.Qty) > 0)
        }
    };
    if (isRelatedObjectDefined(booking)) {
        input.ID = booking.ID;
        input[purchaseOrderField].SupplierID = booking.ContactID || booking.Contact.ID;
    } else if (eventObj && isRelatedObjectDefined(eventObj)) {
        // make sure any relevant relations are added. CMS will use committal for booking details.
        input.CommittalVenueID = funeralId;
        input.CalendarEventID = (eventObj && eventObj.ID) || null;
    }
    if (purchaseOrderField === 'PurchaseOrder') {
        // not a hospital bill, is attached to a booking, so ensure contact ID
        input.ContactID = booking.ContactID || booking.Contact.ID;
    }

    return await getClient().mutate({
        mutation,
        variables: {
            input
        }
    });
};

export const createCertificationPurchaseOrderMutation = gql`
    ${PurchaseOrderObject}
    mutation createCertificationPurchaseOrderMutation($input: UpdateFuneralCertificationInput!) {
        updateFuneralCertification(input: $input) {
            ID
            RefereePurchaseOrder {
                ...PurchaseOrderObject
            }
            HospitalPurchaseOrder {
                ...PurchaseOrderObject
            }
            DoctorPurchaseOrder {
                ...PurchaseOrderObject
            }
        }
    }
`;

export const createBookingPurchaseOrderMutation = gql`
    ${PurchaseOrderBooking}
    mutation createBookingPurchaseOrderMutation($input: UpdateFuneralBookingInput!) {
        updateFuneralBooking(input: $input) {
            ID
            ...PurchaseOrderBooking
        }
    }
`;

export const createNewBookingPurchaseOrderMutation = gql`
    ${PurchaseOrderBooking}
    mutation createNewBookingPurchaseOrderMutation($input: CreateFuneralBookingInput!) {
        createFuneralBooking(input: $input) {
            ID
            ...PurchaseOrderBooking
        }
    }
`;

const styles = ({ palette, spacing, funeralHome, breakpoints }) => ({
    root: {
        maxWidth: '800px',
        width: '100%',
        maxHeight: 'unset',
        [breakpoints.down('sm')]: {
            margin: spacing(1)
        }
    },
    diagHeader: {
        background: palette.contentForeground[funeralHome],
        padding: spacing(3)
    },
    diagHeaderTitle: {
        color: '#FFFFFF',
        fontSize: '16px',
        fontWeight: 'lighter',
        lineHeight: 'normal'
    },
    diagHeaderSubtitle: {
        color: '#FFFFFF',
        fontSize: '36px',
        fontWeight: 'initial',
        lineHeight: 'normal'
    },
    starButton: {
        width: '70px',
        height: '70px',
        float: 'right',
        color: 'rgba(255,255,255,0.5)'
    },
    content: {
        width: '100%',
        padding: spacing(3),
        paddingTop: `${spacing(3)}!important`,
        maxHeight: 'calc(100vh - 15rem)',
        overflow: 'auto',
        minHeight: 340,
        backgroundColor: '#fafafa',
        [breakpoints.down('sm')]: {
            maxHeight: 'calc(100vh - 12rem)',
            padding: spacing(1)
        }
    },
    diagActions: {
        padding: spacing(3),
        paddingTop: spacing(2),
        margin: 0,
        justifyContent: 'space-between'
    },
    regular: {
        fontSize: '16px',
        fontWeight: 'lighter'
    },
    greenButt: {
        border: '1px solid' + palette.button.save,
        backgroundColor: palette.button.save,
        '&:hover': {
            backgroundColor: palette.action.hover,
            color: palette.button.save
        }
    },
    iconButton: {
        width: '18px;',
        height: '18px;'
    },
    icon: {
        width: '14px;',
        height: '14px;'
    },
    dataList: {
        overflowY: 'auto',
        marginTop: 20,
        marginBottom: 30
    }
});

const BillTemplatesQuery = gql`
    ${ProductPriceOverrides}
    ${VariationPriceOverrides}
    query BillTemplatesQuery($ContactID: ID!) {
        readBillTemplates(filter: { ContactID: { in: [$ContactID, 0] } }) {
            edges {
                node {
                    ID
                    NotCurrent
                    Contact {
                        ID
                    }
                    BillTemplateItems {
                        ID
                        Qty
                        Optional
                        Product {
                            ID
                            InternalItemID
                            Title
                            BasePrice
                            GST
                            BaseWorth
                            ITC
                            ...ProductPriceOverrides
                        }
                        Variation {
                            ID
                            InternalItemID
                            ShortDescription
                            Price
                            GST
                            Worth
                            ITC
                            ...VariationPriceOverrides
                        }
                    }
                }
            }
        }
    }
`;

export default compose(withSnackbarMessage, withStyles(styles))(PurchaseOrderModal);
